import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation } from 'react-router-dom';
import { Box,
} from '@chakra-ui/react';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { SocketProvider } from './contexts/SocketContext.jsx';
import HomeGateway from './components/HomeGateway.jsx';
import LandingPage from './components/LandingPage.jsx';
import PlanesPage from './components/PlanesPage.jsx';
import HermandadesLanding from './components/HermandadesLanding.jsx';
import ManualUsuario from './components/ManualUsuario.jsx';
import Register from './components/Register.jsx';
import Login from './components/Login.jsx';
import Profile from './components/Profile.jsx';
import ProfileCofradia from './components/ProfileCofradia.jsx';
import Calendar from './components/Calendar.jsx';
import Search from './components/Search.jsx';
import ProfileDetail from './components/ProfileDetail.jsx';
import Messages from './components/Messages.jsx';
import Events from './components/Events.jsx';
import EventDetails from './components/EventDetails.jsx';
import ComingSoon from './components/ComingSoon.jsx';
import LoadingScreen from './components/LoadingScreen.jsx';
import { MvpProvider, useMvpMode } from './config/mvp.jsx';
import AdminDashboard from './components/AdminDashboard.jsx';
import CookieBanner from './components/CookieBanner.jsx';
import CookiePolicy from './components/CookiePolicy.jsx';
import OtorgarAval from './components/OtorgarAval.jsx';
import DevFooter from './components/DevFooter.jsx';
import SobreNosotros from './components/SobreNosotros.jsx';
import ComoFunciona from './components/ComoFunciona.jsx';
import Contacto from './components/Contacto.jsx';
import TerminosDeUso from './components/TerminosDeUso.jsx';
import PoliticaPrivacidad from './components/PoliticaPrivacidad.jsx';
import Ayuda from './components/Ayuda.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import PlanErrorModal from './components/PlanErrorModal.jsx';
import LoginAlertsModal from './components/LoginAlertsModal.jsx';
import ForgotPassword from './components/ForgotPassword.jsx';
import ResetPassword from './components/ResetPassword.jsx';
import NotFound from './components/NotFound.jsx';
import BandMicrosite from './components/BandMicrosite.jsx';
import BandMicrositePreview from './components/BandMicrositePreview.jsx';
import Formaciones from './components/Formaciones.jsx';
import Actuaciones from './components/Actuaciones.jsx';
import BandaRepertorio from './components/BandaRepertorio.jsx';
import MiSuscripcion from './components/MiSuscripcion.jsx';
import MusicosPage from './components/MusicosPage.jsx';
import Contactos from './components/Contactos.jsx';
import Ensayos from './components/Ensayos.jsx';
import Metricas from './components/Metricas.jsx';
import Objetivos from './components/Objetivos.jsx';
import MetricasResumenPublic from './components/MetricasResumenPublic.jsx';
import EncuestaPublica from './components/EncuestaPublica.jsx';
import EncuestaGrupoPublica from './components/EncuestaGrupoPublica.jsx';
import EncuestasConsultas from './components/EncuestasConsultas.jsx';
import EncuestaConsultaPublica from './components/EncuestaConsultaPublica.jsx';
import RepertorioCompartidoPublic from './components/RepertorioCompartidoPublic.jsx';
import BandaDocumentosGestion from './components/BandaDocumentosGestion.jsx';
import DocumentosBandaPublic from './components/DocumentosBandaPublic.jsx';
import { trackPageView } from './services/analytics';
import { reportarVista } from './services/uso';
import { canAccessGestionModule } from './utils/bandaGestionAccess';
import BandaPermisosGestion from './components/BandaPermisosGestion.jsx';

const BANDA_ACCOUNT_TYPES = ['BANDA', 'BANDA_COLABORADOR'];

const RESERVED_SUBDOMAINS = [
  'www', 'dev', 'staging', 'test', 'demo',
  'blog', 'sentry', 'grafana', 'adminer', 'odoo',
  'portainer', 'traefik-dash', 'api', 'admin', 'app',
  'mail', 'email', 'help', 'support', 'status',
];

function getMicrositeSlug() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Match *.cadencya.com (3 parts: slug.cadencya.com)
  if (parts.length === 3 && parts[1] === 'cadencya' && parts[2] === 'com') {
    const subdomain = parts[0].toLowerCase();
    if (!RESERVED_SUBDOMAINS.includes(subdomain)) {
      return subdomain;
    }
  }

  // Match *.dev.cadencya.com (4 parts: slug.dev.cadencya.com)
  if (parts.length === 4 && parts[1] === 'dev' && parts[2] === 'cadencya' && parts[3] === 'com') {
    const subdomain = parts[0].toLowerCase();
    if (!RESERVED_SUBDOMAINS.includes(subdomain)) {
      return subdomain;
    }
  }

  return null;
}

// Componente para trackear pageviews en la SPA
const AnalyticsPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
    reportarVista(location.pathname);
  }, [location]);
  return null;
};

// Componente para rutas protegidas
const ProtectedRoute = ({ children, allowedUserTypes }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedUserTypes && !allowedUserTypes.includes(user?.userType)) {
    // Redirigir al perfil correcto según el tipo de usuario
    let redirectPath = '/banda/perfil';
    if (user?.userType === 'COFRADIA') {
      redirectPath = '/cofradia/perfil';
    } else if (user?.userType === 'ADMIN') {
      redirectPath = '/admin';
    }
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

/** Solo titular de la banda (no cuentas delegadas). */
const TitularBandaOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user?.userType === 'BANDA_COLABORADOR') {
    return <Navigate to="/banda/perfil" replace />;
  }
  return children;
};

/** Comprueba `gestionPermissions` en el cliente (el API también valida). */
const GestionModuleGate = ({ permissionKey, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!canAccessGestionModule(user, permissionKey)) {
    return <Navigate to="/banda/perfil" replace />;
  }
  return children;
};

// Componente para rutas de autenticación (login/registro)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, user, showPostLoginSplash, completePostLoginSplash } = useAuth();

  // NO usamos loading aquí para evitar que se desmonte el componente
  // durante operaciones de login/registro

  if (isAuthenticated) {
    if (showPostLoginSplash) {
      return (
        <LoadingScreen
          message="Iniciando sesión..."
          duration={2000}
          onComplete={completePostLoginSplash}
        />
      );
    }
    // Redirigir al perfil según el tipo de usuario
    let redirectPath = '/banda/perfil';
    if (user?.userType === 'COFRADIA') {
      redirectPath = '/cofradia/perfil';
    } else if (user?.userType === 'ADMIN') {
      redirectPath = '/admin';
    } else if (user?.userType === 'BANDA_COLABORADOR') {
      redirectPath = '/banda/perfil';
    }
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

const HomeRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (isAuthenticated) {
    let redirectPath = '/banda/perfil';
    if (user?.userType === 'COFRADIA') redirectPath = '/cofradia/perfil';
    else if (user?.userType === 'ADMIN') redirectPath = '/admin';
    else if (user?.userType === 'BANDA_COLABORADOR') redirectPath = '/banda/perfil';
    return <Navigate to={redirectPath} replace />;
  }

  return <HomeGateway />;
};

function AppRoutes() {
  const { isMvpMode } = useMvpMode();
  return (
    <Box>
      <Routes>
        {/* Puerta de entrada: redirige al perfil si ya está autenticado */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/bandas" element={<LandingPage />} />
        <Route path="/planes" element={<PlanesPage />} />
        <Route path="/hermandades" element={<HermandadesLanding />} />
        <Route path="/manual" element={<ManualUsuario />} />
        <Route path="/politica-cookies" element={<CookiePolicy />} />
        <Route path="/avales/otorgar" element={<OtorgarAval />} />
        <Route path="/sobre-nosotros" element={<SobreNosotros />} />
        <Route path="/como-funciona" element={<ComoFunciona />} />
        <Route path="/contacto" element={<Contacto />} />
        <Route path="/terminos-de-uso" element={<TerminosDeUso />} />
        <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/ayuda" element={<Ayuda />} />
        
        {/* Rutas de autenticación */}
        <Route 
          path="/registro" 
          element={
            <AuthRoute>
              <Register />
            </AuthRoute>
          } 
        />
        <Route 
          path="/login" 
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          } 
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Rutas de Banda */}
        <Route 
          path="/banda/perfil" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/banda/suscripcion"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <MiSuscripcion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/banda/microsite-preview"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="tuWeb">
                <BandMicrositePreview />
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/banda/repertorio" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="repertorio">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <BandaRepertorio />}
              </GestionModuleGate>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/banda/documentos"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="documentos">
                {isMvpMode ? (
                  <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" />
                ) : (
                  <BandaDocumentosGestion />
                )}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/banda/calendario" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <TitularBandaOnly>
                <Calendar />
              </TitularBandaOnly>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/banda/eventos" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <TitularBandaOnly>
                {isMvpMode ? <ComingSoon title="Eventos - Próximamente" message="La gestión de eventos estará disponible muy pronto" userType="banda" /> : <Events userType="banda" />}
              </TitularBandaOnly>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/banda/eventos/:eventId" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <TitularBandaOnly>
                {isMvpMode ? <ComingSoon title="Eventos - Próximamente" message="La gestión de eventos estará disponible muy pronto" userType="banda" /> : <EventDetails userType="banda" />}
              </TitularBandaOnly>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/banda/mensajes" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <TitularBandaOnly>
                {isMvpMode ? <ComingSoon title="Mensajería - Próximamente" message="El sistema de mensajería estará disponible muy pronto" userType="banda" /> : <Messages userType="banda" />}
              </TitularBandaOnly>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/banda/buscar" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <TitularBandaOnly>
                <Search userType="banda" />
              </TitularBandaOnly>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/banda/musicos"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="musicos">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <MusicosPage />}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/banda/formaciones" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="formaciones">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <Formaciones />}
              </GestionModuleGate>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/banda/actuaciones" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="actuaciones">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <Actuaciones />}
              </GestionModuleGate>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/banda/contactos"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="contactos">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <Contactos />}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/banda/ensayos"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="ensayos">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <Ensayos />}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/banda/metricas"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="estadisticas">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <Metricas />}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/banda/objetivos"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="estadisticas">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <Objetivos />}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/banda/encuestas-consultas"
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <GestionModuleGate permissionKey="encuestas">
                {isMvpMode ? <ComingSoon title="Gestión - Próximamente" message="La sección de Gestión estará disponible muy pronto" userType="banda" /> : <EncuestasConsultas />}
              </GestionModuleGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/banda/permisos"
          element={
            <ProtectedRoute allowedUserTypes={['BANDA']}>
              <BandaPermisosGestion />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/banda/ver-perfil/:id" 
          element={
            <ProtectedRoute allowedUserTypes={BANDA_ACCOUNT_TYPES}>
              <TitularBandaOnly>
                <ProfileDetail userType="banda" />
              </TitularBandaOnly>
            </ProtectedRoute>
          } 
        />
        
        {/* Rutas de Cofradía */}
        <Route 
          path="/cofradia/perfil" 
          element={
            <ProtectedRoute allowedUserTypes={['COFRADIA']}>
              <ProfileCofradia />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cofradia/eventos" 
          element={
            <ProtectedRoute allowedUserTypes={['COFRADIA']}>
              {isMvpMode ? <ComingSoon title="Eventos - Próximamente" message="La gestión de eventos estará disponible muy pronto" userType="cofradia" /> : <Events userType="cofradia" />}
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cofradia/eventos/:eventId" 
          element={
            <ProtectedRoute allowedUserTypes={['COFRADIA']}>
              {isMvpMode ? <ComingSoon title="Eventos - Próximamente" message="La gestión de eventos estará disponible muy pronto" userType="cofradia" /> : <EventDetails userType="cofradia" />}
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cofradia/mensajes" 
          element={
            <ProtectedRoute allowedUserTypes={['COFRADIA']}>
              {isMvpMode ? <ComingSoon title="Mensajería - Próximamente" message="El sistema de mensajería estará disponible muy pronto" userType="cofradia" /> : <Messages userType="cofradia" />}
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cofradia/buscar" 
          element={
            <ProtectedRoute allowedUserTypes={['COFRADIA']}>
              <Search userType="cofradia" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cofradia/ver-perfil/:id" 
          element={
            <ProtectedRoute allowedUserTypes={['COFRADIA']}>
              <ProfileDetail userType="cofradia" />
            </ProtectedRoute>
          } 
        />
        
        {/* Rutas públicas para ver perfiles y Comunidad (sin login) */}
        <Route path="/ver-perfil-banda" element={<ProfileDetail />} />
        <Route path="/ver-perfil-cofradia" element={<ProfileDetail />} />
        <Route path="/comunidad" element={<Search userType="banda" />} />
        
        {/* Encuesta publica */}
        <Route path="/encuesta/:token" element={<EncuestaPublica />} />
        <Route path="/encuesta-grupo/:shareToken" element={<EncuestaGrupoPublica />} />
        <Route path="/consulta/:shareToken" element={<EncuestaConsultaPublica />} />
        <Route path="/consulta/r/:convocadoToken" element={<EncuestaConsultaPublica />} />

        <Route path="/repertorio-compartido/:token" element={<RepertorioCompartidoPublic />} />
        <Route path="/documentos-banda/:token" element={<DocumentosBandaPublic />} />
        <Route path="/metricas/resumen" element={<MetricasResumenPublic />} />
        
        {/* Rutas de Admin */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute allowedUserTypes={['ADMIN']}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Rutas de compatibilidad (redirigen a las nuevas rutas) */}
        <Route path="/perfil" element={<Navigate to="/banda/perfil" replace />} />
        <Route path="/perfil-banda" element={<Navigate to="/banda/perfil" replace />} />
        <Route path="/perfil-cofradia" element={<Navigate to="/cofradia/perfil" replace />} />
        <Route path="/calendario" element={<Navigate to="/banda/calendario" replace />} />
        <Route path="/buscar" element={<Navigate to="/banda/buscar" replace />} />
        
        {/* Ruta 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Box>
  );
}

function App() {
  const micrositeSlug = getMicrositeSlug();

  if (micrositeSlug) {
    return <BandMicrosite slug={micrositeSlug} />;
  }

  return (
    <MvpProvider>
      <Router>
        <ScrollToTop />
        <AnalyticsPageTracker />
        <AuthProvider>
          <SocketProvider>
            <AppRoutes />
            <PlanErrorModal />
            <LoginAlertsModal />
            <CookieBanner />
            <DevFooter />
          </SocketProvider>
        </AuthProvider>
      </Router>
    </MvpProvider>
  );
}

export default App;
